import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Circle } from "lucide-react";
import { api } from "../lib/api";

export default function GetStarted({ role, streak }) {
    const { t } = useTranslation();
    const [counts, setCounts] = useState({ first: 0, projects: 0, deadlines: 0 });
    const isStudent = role === "student";

    useEffect(() => {
        const firstRes = isStudent ? "/classes" : "/clients";
        Promise.all([api.get(firstRes), api.get("/projects")])
            .then(([a, b]) => {
                const firstCount = a.data.length;
                const projectsCount = b.data.length;
                const deadlinesCount = b.data.filter(p => p.end_date).length;
                setCounts({ first: firstCount, projects: projectsCount, deadlines: deadlinesCount });
            }).catch(() => {});
    }, [isStudent]);

    const steps = [
        {
            done: counts.first > 0,
            label: isStudent ? t("getStarted.addFirstClass") : t("getStarted.addFirstClient"),
            to: isStudent ? "/classes" : "/clients",
        },
        { done: counts.projects > 0, label: t("getStarted.createFirstProject"), to: "/projects" },
        { done: counts.deadlines > 0, label: t("getStarted.setDeadline"), to: "/projects" },
        { done: (streak?.current ?? 0) >= 3, label: t("getStarted.hit3Day"), to: "/dashboard" },
    ];
    const completed = steps.filter(s => s.done).length;
    const pct = Math.round((completed / steps.length) * 100);

    // Hide if all done
    if (completed === steps.length) return null;

    return (
        <div className="card-warm p-5 mb-8 fade-up" data-testid="get-started-card">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="font-script text-[var(--brand-orange)] text-lg">{t("getStarted.eyebrow")}</div>
                    <h2 className="font-serif text-2xl">{t("getStarted.title")}</h2>
                </div>
                <div className="text-right">
                    <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/55">{t("getStarted.progress")}</div>
                    <div className="font-serif text-2xl" style={{ color: "var(--brand-orange)" }}>{completed}/{steps.length}</div>
                </div>
            </div>
            <div className="h-2 rounded-full bg-[rgba(26,19,24,0.08)] overflow-hidden mb-4">
                <div className="h-full brand-gradient transition-all" style={{ width: `${pct}%` }} />
            </div>
            <ul className="grid sm:grid-cols-2 gap-2">
                {steps.map((s, i) => (
                    <li key={i}>
                        <Link to={s.to} data-testid={`get-started-step-${i}`}
                              className={`flex items-center gap-3 p-3 rounded-xl transition ${s.done ? "bg-[var(--status-green)]/8" : "hover:bg-[rgba(255,107,53,0.08)]"}`}>
                            {s.done ? (
                                <Check size={18} className="text-[var(--status-green)] shrink-0" />
                            ) : (
                                <Circle size={18} className="text-[var(--text-ink)]/30 shrink-0" />
                            )}
                            <span className={`text-sm ${s.done ? "line-through text-[var(--text-ink)]/45" : "text-[var(--text-ink)]"}`}>{s.label}</span>
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}
